import type { IMemberSearch } from '@/client/legacy-shared-modules/search/search-types';

type Interval = { start: number; end: number };

function getHighlightResultNode(hit: IMemberSearch, attribute: string): unknown {
  const hr = (hit as unknown as { _highlightResult?: Record<string, unknown> })
    ._highlightResult;
  if (!hr) {
    return undefined;
  }
  return attribute.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') {
      return undefined;
    }
    return (acc as Record<string, unknown>)[key];
  }, hr as unknown);
}

function addMatchedWordsFromNode(
  node: unknown,
  into: Set<string>
): void {
  if (!node || typeof node !== 'object') {
    return;
  }
  const mw = (node as { matchedWords?: unknown }).matchedWords;
  if (!Array.isArray(mw)) {
    return;
  }
  for (const w of mw) {
    if (typeof w === 'string' && w.trim()) {
      into.add(w.trim());
    }
  }
}

const NAME_SUB_FIELDS = [
  'preferredName',
  'firstName',
  'lastName',
  'prefix',
  'suffix',
  'nameTitle',
] as const;

/**
 * Union of `matchedWords` from member `_highlightResult` (displayName + viewable/searchable name fields).
 */
export function collectMemberMatchedWords(
  hit: IMemberSearch,
  searchablePrefix: string,
  viewablePrefix: string
): string[] {
  const words = new Set<string>();
  addMatchedWordsFromNode(getHighlightResultNode(hit, 'displayName'), words);
  const prefixes = [...new Set([searchablePrefix, viewablePrefix].filter(Boolean))];
  for (const prefix of prefixes) {
    for (const sub of NAME_SUB_FIELDS) {
      addMatchedWordsFromNode(getHighlightResultNode(hit, `${prefix}.${sub}`), words);
    }
  }
  return [...words];
}

function mergeIntervals(intervals: Interval[]): Interval[] {
  if (!intervals.length) {
    return [];
  }
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const out: Interval[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const last = out[out.length - 1];
    if (cur.start < last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/**
 * Case-insensitive occurrences of each word in `text`; overlapping ranges merged.
 */
export function findMergedMatchIntervals(
  text: string,
  words: string[]
): Interval[] {
  const lower = text.toLowerCase();
  const intervals: Interval[] = [];
  const sortedWords = [...words].sort((a, b) => b.length - a.length);

  for (const raw of sortedWords) {
    const w = raw.trim();
    if (!w) {
      continue;
    }
    const lw = w.toLowerCase();
    let from = 0;
    while (from <= text.length - lw.length) {
      const idx = lower.indexOf(lw, from);
      if (idx === -1) {
        break;
      }
      intervals.push({ start: idx, end: idx + w.length });
      from = idx + 1;
    }
  }
  return mergeIntervals(intervals);
}

export function splitTextByHighlightIntervals(
  text: string,
  intervals: Interval[]
): Array<{ value: string; isHighlighted: boolean }> {
  if (!intervals.length) {
    return [{ value: text, isHighlighted: false }];
  }
  const parts: Array<{ value: string; isHighlighted: boolean }> = [];
  let cursor = 0;
  for (const m of intervals) {
    if (m.start > cursor) {
      parts.push({
        value: text.slice(cursor, m.start),
        isHighlighted: false,
      });
    }
    parts.push({
      value: text.slice(m.start, m.end),
      isHighlighted: true,
    });
    cursor = m.end;
  }
  if (cursor < text.length) {
    parts.push({ value: text.slice(cursor), isHighlighted: false });
  }
  return parts;
}
